<!--
  `#/home/alphabet` — learn the script the boxes are drawn around.

  A reference chart of the Hismaic letter forms, and flashcards to drill them. The forms are
  the OCIANA font's renderings (there is no Hismaic font in the repo and no letter imagery on
  S3 — only these), from two sources: the record pages cached in this repository, and, for the
  four letters none of those pages happens to contain, snapshots of the original OCIANA site
  held by the Internet Archive.

  Every count and letter list below is read from the generated artifact rather than written
  into the markup. This panel used to say "24 of the 28" as a literal; the number is a
  property of what was scraped, and a learning aid that states a stale figure — or quietly
  fills its own gaps — teaches the wrong thing.
-->
<script lang="ts">
  import { ALPHABET_SOURCE } from '../lib/alphabet.generated';
  import { href } from '../lib/router.svelte';
  import AlphabetChart from './AlphabetChart.svelte';
  import AlphabetDrill from './AlphabetDrill.svelte';

  const ARCHIVE_DOC = 'tools/hismaic_annotator/docs/glyph_archive.md';
  const missing = ALPHABET_SOURCE.missingLetters.join(' ');
  const missingCount = ALPHABET_SOURCE.attestedLetters - ALPHABET_SOURCE.coveredLetters;
</script>

<div class="col panels">
  <section class="panel col" aria-labelledby="alphabet-intro" data-section="intro">
    <h2 id="alphabet-intro">The Hismaic alphabet</h2>
    <p class="small">
      Hismaic is an Ancient North Arabian script, written in a South-Semitic-family alphabet and
      <strong>multidirectional</strong> — letters mirror with the direction of writing. The forms
      below are the OCIANA font's renderings, recovered from the cached record pages; they are a
      reference for reading the transliteration, not a substitute for the stone.
    </p>
    <p class="small">
      <strong
        >{ALPHABET_SOURCE.coveredLetters} of the {ALPHABET_SOURCE.attestedLetters} attested
        letters</strong
      >
      have a reference form.
      {#if missingCount > 0}
        The other {missingCount} (<span class="glyph">{missing}</span>) have no form in either
        source, so they are shown as explicit gaps and left out of the flashcards — never filled
        in from a look-alike.
      {:else}
        Four of them (<span class="glyph">ḏ ġ k ẓ</span>) appear on no cached page and were
        recovered from Internet Archive snapshots of the original OCIANA site — the same
        renderer, verified byte-identical on a page held both ways, and each letter confirmed
        against two independent records. The chart labels which source each form came from; the
        provenance record is <span class="mono">{ARCHIVE_DOC}</span>.
      {/if}
      Full letter-identity conventions are in
      <a href={href({ name: 'guidelines', at: null })}>the annotation guidelines</a>.
    </p>
  </section>

  <AlphabetDrill />
  <AlphabetChart />
</div>

<style>
  .panels {
    gap: 12px;
  }

  p {
    margin: 0;
    max-width: 82ch;
  }
</style>
